export class IdentityApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Create a new identity.
     *
     * @param params - Identity creation parameters
     * @returns Created identity on success
     */
    async create(params) {
        return this.client.post('/api/identity', params);
    }
    /**
     * Pre-flight a username and/or display name before creating an identity.
     *
     * Checks shape, the blocked-word list, and (for usernames) availability. The
     * create endpoint re-runs all of it, so a pass here is advisory — a name can
     * still be taken in the window before submission.
     *
     * POST rather than GET so candidate names stay out of access logs and caches.
     */
    async checkName(params) {
        return this.client.post('/api/identity/name-check', params);
    }
    /**
     * Login to an identity using passphrase.
     *
     * On success, sets an identity session cookie.
     *
     * @param params - Login parameters with passphrase
     * @returns Identity info on success, error with retry info on failure
     */
    async login(params) {
        return this.client.post('/api/identity/login', params);
    }
    /**
     * Logout from the current identity session.
     *
     * Clears the identity session cookie.
     *
     * @returns Success on logout
     */
    async logout() {
        return this.client.post('/api/identity/logout', {});
    }
    /**
     * Get the current identity session.
     *
     * @returns Current identity if logged in, error if not
     */
    async getSession() {
        return this.client.get('/api/identity/session');
    }
    /**
     * Delete the current identity (soft delete).
     *
     * The identity record is preserved for historical purposes,
     * but the passphrase hash is cleared.
     *
     * @returns Success on deletion
     */
    async delete() {
        return this.client.delete('/api/identity');
    }
    /**
     * Search for identities by username or display name.
     *
     * Public endpoint - no authentication required.
     *
     * @param query - Search query (min 2 characters)
     * @param limit - Max results (default: 10, max: 50)
     * @returns Array of matching identities
     */
    async search(query, limit) {
        const params = new URLSearchParams({ q: query });
        if (limit !== undefined) {
            params.set('limit', limit.toString());
        }
        return this.client.get(`/api/identity/search?${params.toString()}`);
    }
    /**
     * Random sample of public identities for directory empty state.
     * Public endpoint - no authentication required.
     */
    async discover(limit) {
        const params = new URLSearchParams();
        if (limit !== undefined) {
            params.set('limit', limit.toString());
        }
        const query = params.toString();
        return this.client.get(`/api/identity/discover${query ? `?${query}` : ''}`);
    }
    /**
     * Get a public identity by ID.
     *
     * Public endpoint - no authentication required.
     *
     * @param id - Identity ID
     * @returns Identity profile
     */
    async getById(id) {
        return this.client.get(`/api/identity/${encodeURIComponent(id)}`);
    }
    /**
     * Initialize E2E encryption for an identity.
     *
     * Atomic operation that sets up E2E: stores signing public key,
     * uploads encrypted bundle, and registers the first device.
     *
     * @param identityId - Identity ID
     * @param params - E2E initialization parameters
     * @returns Success on initialization
     */
    async initializeE2E(identityId, params) {
        return this.client.post(`/api/identity/${encodeURIComponent(identityId)}/e2e/initialize`, params);
    }
    /**
     * Get public keys for an identity (for encryption).
     *
     * Returns the signing public key plus device keys. Requires the caller to be
     * self, a friend, or share a conversation with the target. Passing `spaceId`
     * additionally allows a member of that Space to read the target's signing key
     * on its own (no devices), which is what Space message signature verification
     * needs. Membership is checked server-side for both identities.
     *
     * @param identityId - Identity ID
     * @param options - Optional Space scope plus standard request options
     * @returns Public keys for E2E encryption
     */
    async getPublicKeys(identityId, options) {
        const { spaceId, ...requestOptions } = options ?? {};
        const query = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/keys${query}`, requestOptions);
    }
    /**
     * Get the encrypted key bundle for an identity.
     *
     * Only the identity owner can access their bundle.
     *
     * @param identityId - Identity ID
     * @returns Encrypted key bundle
     */
    async getKeyBundle(identityId) {
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/bundle`);
    }
    /**
     * Update the encrypted key bundle.
     *
     * Used when rotating encryption or changing passphrase.
     *
     * @param identityId - Identity ID
     * @param params - New encrypted bundle data
     * @returns Success on update
     */
    async updateKeyBundle(identityId, params) {
        return this.client.put(`/api/identity/${encodeURIComponent(identityId)}/bundle`, params);
    }
    /**
     * Publish ML-DSA-65 verifying key and store a v3 encrypted bundle.
     *
     * First writer wins. A different already-published PQ key returns CONFLICT
     * so the client can refetch the upgraded bundle.
     */
    async upgradePqSigning(identityId, params) {
        return this.client.put(`/api/identity/${encodeURIComponent(identityId)}/e2e/pq-signing`, params);
    }
    /**
     * Register a new device for E2E encryption.
     *
     * @param identityId - Identity ID
     * @param params - Device registration parameters
     * @returns Success on registration
     */
    async registerDevice(identityId, params) {
        return this.client.post(`/api/identity/${encodeURIComponent(identityId)}/devices`, params);
    }
    /**
     * List all devices for an identity.
     *
     * Only the identity owner can list their devices.
     *
     * @param identityId - Identity ID
     * @returns Object containing array of registered devices
     */
    async listDevices(identityId) {
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/devices`);
    }
    /**
     * Remove a device from an identity.
     *
     * @param identityId - Identity ID
     * @param deviceId - Device ID to remove
     * @returns Success on removal
     */
    async removeDevice(identityId, deviceId) {
        return this.client.delete(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}`);
    }
    /**
     * Update a device (name and/or activity).
     *
     * @param identityId - Identity ID
     * @param deviceId - Device ID to update
     * @param params - Update parameters
     * @returns Success on update
     */
    async updateDevice(identityId, deviceId, params) {
        return this.client.patch(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}`, params);
    }
    /**
     * Upload Ed25519 attestation over this device's static public keys (device-trust v3).
     */
    async putDeviceStaticKeyAttestation(identityId, deviceId, params) {
        return this.client.put(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}/static-key-attestation`, params);
    }
    /**
     * Update device activity (heartbeat).
     *
     * @param identityId - Identity ID
     * @param deviceId - Device ID
     * @returns Success on update
     */
    async updateDeviceActivity(identityId, deviceId) {
        return this.client.patch(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}`, { updateActivity: true });
    }
    /**
     * Rename a device.
     *
     * @param identityId - Identity ID
     * @param deviceId - Device ID
     * @param name - New device name
     * @returns Success on update
     */
    async renameDevice(identityId, deviceId, name) {
        return this.client.patch(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}`, { name });
    }
    /**
     * Upload pre-keys for a device (signed pre-key and/or one-time pre-keys).
     */
    async uploadPreKeys(identityId, deviceId, params) {
        return this.client.post(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}/pre-keys`, params);
    }
    /**
     * Claim pre-keys for all (or specified) devices of an identity.
     * Used by senders before encrypting a message.
     */
    async claimPreKeys(identityId, params, requestOptions) {
        return this.client.post(`/api/identity/${encodeURIComponent(identityId)}/pre-keys/claim`, params ?? {}, requestOptions);
    }
    /**
     * Get remaining pre-key counts for a device.
     */
    async getPreKeyCount(identityId, deviceId) {
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}/pre-keys/count`);
    }
    /**
     * Purge all unconsumed one-time pre-keys for a device on the server.
     * Used to reset the OTPK pool when local and server state have diverged.
     */
    async purgeOneTimePreKeys(identityId, deviceId) {
        return this.client.delete(`/api/identity/${encodeURIComponent(identityId)}/devices/${encodeURIComponent(deviceId)}/pre-keys/one-time`);
    }
    /**
     * List all identity sessions.
     *
     * @param identityId - Identity ID
     * @returns Object containing array of active sessions
     */
    async listSessions(identityId) {
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/sessions`);
    }
    /**
     * Revoke a specific identity session.
     * Cannot revoke the current session.
     *
     * @param identityId - Identity ID
     * @param sessionId - Session ID to revoke
     * @returns Success on revocation
     */
    async revokeIdentitySession(identityId, sessionId) {
        return this.client.delete(`/api/identity/${encodeURIComponent(identityId)}/sessions/${encodeURIComponent(sessionId)}`);
    }
    /**
     * Revoke all other identity sessions (except the current one).
     *
     * @param identityId - Identity ID
     * @returns Count of revoked sessions
     */
    async revokeAllOtherIdentitySessions(identityId) {
        return this.client.delete(`/api/identity/${encodeURIComponent(identityId)}/sessions`);
    }
    /**
     * Update own profile (display name, bio, avatar, banner, colours, privacy).
     */
    async updateProfile(params) {
        return this.client.patch('/api/identity/me/profile', params);
    }
    /** Current alias session maximum and when this device will be forced to sign out. */
    async getSessionLifetime() {
        return this.client.get('/api/identity/me/session-lifetime');
    }
    /** Set the alias session maximum. Applies immediately. */
    async updateSessionLifetime(maxAgeDays) {
        return this.client.put('/api/identity/me/session-lifetime', { maxAgeDays });
    }
    /** Alias-wide notification settings (Space defaults and notification detail). */
    async getNotificationSettings() {
        return this.client.get('/api/identity/me/notification-settings');
    }
    async updateNotificationSettings(params) {
        return this.client.put('/api/identity/me/notification-settings', params);
    }
    /**
     * Get a privacy-filtered profile for an identity.
     *
     * Fields are filtered server-side based on the viewer's relationship
     * to the profile owner (self, friend, or stranger).
     */
    async getProfile(identityId) {
        return this.client.get(`/api/identity/${encodeURIComponent(identityId)}/profile`);
    }
    /**
     * Get the privacy-filtered friends list for an identity's profile.
     *
     * Returns `{ friends, hidden, count, cursor }` where `hidden` is true when
     * the viewer does not have permission to see this identity's friends.
     * Supports cursor-based pagination and server-side search.
     */
    async getIdentityFriends(identityId, params) {
        const qs = new URLSearchParams();
        if (params?.limit != null)
            qs.set('limit', String(params.limit));
        if (params?.cursor)
            qs.set('cursor', params.cursor);
        if (params?.q)
            qs.set('q', params.q);
        const query = qs.toString();
        const url = `/api/identity/${encodeURIComponent(identityId)}/friends${query ? `?${query}` : ''}`;
        return this.client.get(url);
    }
    /**
     * Change the identity passphrase.
     *
     * The client must re-encrypt the key bundle with the new passphrase
     * before calling this endpoint.
     */
    async changePassphrase(params) {
        return this.client.post('/api/identity/change-passphrase', params);
    }
    /**
     * Fetch encrypted key bundle using account session + passphrase proof.
     *
     * Used during passphrase change flows when signed into the account
     * (not the alias). The signedToken proves account ownership and the
     * passphrase is used server-side to derive the bundle lookup key.
     */
    async bundleByPassphrase(params) {
        return this.client.post('/api/identity/bundle-by-passphrase', params);
    }
}
//# sourceMappingURL=identity-api.js.map